import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useListManagedJobOpeningsQuery } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

// Mirrored from the employee apply form so a manager sees the same wording the
// applicant answered.
const APPLICATION_QUESTIONS = [
  { key: 'reason_interested', label: 'Why interested in this position?' },
  { key: 'strengths', label: 'Skills, experience & accomplishments' },
  { key: 'growth_alignment', label: 'Alignment with growth & career goals' },
];
const APPLICATION_ACKS = [
  { key: 'ack_no_guarantee', label: 'Understands application does not guarantee an interview or promotion' },
  { key: 'ack_transition_coordinated', label: 'Understands transition date is coordinated between departments' },
  { key: 'ack_reviewed_requirements', label: 'Reviewed the job description and meets the minimum requirements' },
];

const POSITION_LABELS = { in_office: 'In office', hybrid: 'Hybrid', remote: 'Remote' };
const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time' };

// One submitted application: applicant identity, their answers, and the
// acknowledgement state. Collapsed to just the identity header by default —
// answers can be long, so the manager expands the ones they want to read.
function ApplicationCard({ app }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: 'white', border: '1px solid #e2ddd4', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', cursor: 'pointer' }}
      >
        <div>
          <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} style={{ color: brand.charcoal, fontSize: '0.75rem' }} />
          <span style={{ fontWeight: 700, color: brand.deepBlue }}>{app.applicant_name}</span>
          {app.applicant_email && (
            <a
              href={`mailto:${app.applicant_email}`}
              onClick={(e) => e.stopPropagation()}
              style={{ color: brand.tealBlue, fontSize: '0.8rem', marginLeft: '0.5rem' }}
            >
              {app.applicant_email}
            </a>
          )}
        </div>
        {app.created && (
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            Applied {new Date(app.created).toLocaleDateString()}
          </span>
        )}
      </div>

      {open && (
        <>
          <div style={{ marginTop: '0.6rem' }}>
            {APPLICATION_QUESTIONS.map((q) => (
              <div key={q.key} style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontWeight: 700, color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{q.label}</div>
                <div style={{ color: brand.deepBlue, fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                  {app[q.key] || <span style={{ color: '#b9c3cd' }}>—</span>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {APPLICATION_ACKS.map((a) => (
              <div key={a.key} style={{ fontSize: '0.76rem', color: app[a.key] ? brand.green : brand.red }}>
                <i className={`bi bi-${app[a.key] ? 'check-circle-fill' : 'x-circle'} me-1`} />{a.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
ApplicationCard.propTypes = { app: PropTypes.object.isRequired };

function MetaChip({ icon, children }) {
  if (!children) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      background: '#eef2f6', color: brand.charcoal, fontSize: '0.75rem', fontWeight: 600,
      padding: '0.2rem 0.55rem', borderRadius: '999px',
    }}>
      <i className={`bi bi-${icon}`} /> {children}
    </span>
  );
}
MetaChip.propTypes = { icon: PropTypes.string, children: PropTypes.string };

// One managed posting with its applicant list. Collapsed by default; the
// header shows the applicant count so a manager can scan across postings.
function PostingCard({ opening }) {
  const apps = opening.applications || [];
  const [open, setOpen] = useState(apps.length > 0);

  return (
    <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', marginBottom: '1rem' }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div>
            <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0, fontSize: '1.1rem' }}>{opening.job_title}</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
              <MetaChip icon="geo-alt">{opening.location}</MetaChip>
              <MetaChip icon="building">{POSITION_LABELS[opening.position_type]}</MetaChip>
              <MetaChip icon="clock">{EMPLOYMENT_LABELS[opening.employment_type]}</MetaChip>
              {opening.is_intern && <MetaChip icon="mortarboard">Internship</MetaChip>}
            </div>
          </div>
          <button
            className="btn btn-sm"
            style={{ background: apps.length ? brand.deepBlue : '#9aa6b1', color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}
            onClick={() => setOpen((o) => !o)}
            disabled={!apps.length}
            title={apps.length ? (open ? 'Hide applicants' : 'Show applicants') : 'No applicants yet'}
          >
            <i className="bi bi-people-fill me-1" />
            {apps.length} applicant{apps.length === 1 ? '' : 's'}
          </button>
        </div>

        {open && apps.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            {apps.map((a) => <ApplicationCard key={a.id} app={a} />)}
          </div>
        )}
        {apps.length === 0 && (
          <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
            No applications yet.
          </p>
        )}
      </div>
    </div>
  );
}
PostingCard.propTypes = { opening: PropTypes.object.isRequired };

// Manager-facing view of applicants to the postings the current user is the
// hiring manager for. Deliberately outside HR/admin — the API scopes results to
// postings the caller manages and hides filled/inactive ones.
export default function ManagerApplicants() {
  const { data, isLoading } = useListManagedJobOpeningsQuery();
  const openings = data?.openings || [];

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>My Job Applicants</h4>
        <p style={{ color: brand.charcoal, margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
          Applicants to the openings you’re hiring for. Filled or closed postings drop off automatically.
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      ) : openings.length === 0 ? (
        <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px' }}>
          <div className="card-body" style={{ color: brand.charcoal, fontSize: '0.9rem' }}>
            You don’t have any active job postings right now.
          </div>
        </div>
      ) : (
        openings.map((o) => <PostingCard key={o.id} opening={o} />)
      )}
    </div>
  );
}
